/**
 * The pure display helpers and the query → state ladder behind `AttachmentList` — size
 * formatting, the media-type label, the copy, and the decision about what the list renders.
 *
 * They live beside the component rather than inside it for the same reason `inlineMessageState.ts`
 * does: a file that exports both a component and plain functions loses React Fast Refresh, and
 * each of these is worth unit-testing on its own. Keeping the ladder here is also what lets
 * `AttachmentList` stay purely presentational — this module knows about `ApiError`, i.e. about
 * the door; the component does not.
 */
import { ApiError } from '../../api/client';

/** 1,139,599 → "1.1 MB". Null size renders nothing rather than "0 B" — a provider that did not
 *  report a size is not a zero-byte file (spec §2.1: provider metadata and actual length differ). */
export function formatSize(bytes: number | null | undefined): string | null {
  if (bytes == null || !Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

/** A short human label for the media type. Deliberately a small, closed map with the raw subtype as
 *  the fallback: a long MIME string in a dense row is noise, and inventing a friendly name for a
 *  type we do not recognize is how "application/x-msdownload" becomes "Document". */
export function typeLabel(contentType: string | null | undefined): string {
  const mime = (contentType || '').toLowerCase();
  const known: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'Word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'text/plain': 'Text',
    'text/csv': 'CSV',
  };
  if (known[mime]) return known[mime];
  const sub = mime.split('/')[1] || '';
  return sub ? sub.split('.').pop()!.toUpperCase().slice(0, 12) : 'File';
}

/** A byte click that failed says only that it failed. It never describes a threat, and it never
 *  offers a Graph retry button after what may have been a permission refusal (spec §3.2). The
 *  authoritative reason arrives on the next status read. */
export const FETCH_FAILED_COPY = "Couldn't open that file. Reopen the message to refresh its state.";

// ── the query → list-state ladder ───────────────────────────────────────────────────────────
// Deliberately the same shape as `inlineMessageStateFromQuery`, and here for the same reason: the
// component stays presentational and this module knows about `ApiError`, i.e. about the door.

/** What the shared list should render for the CURRENT state of a surface's own content query. */
export type AttachmentListState = 'loading' | 'forbidden' | 'missing' | 'error' | 'ready';

/** The part of a React Query result this decision needs. */
export interface AttachmentQueryLike {
  isLoading?: boolean;
  isFetching?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  error?: unknown;
  data?: unknown;
}

export function attachmentListStateFromQuery(q: AttachmentQueryLike): AttachmentListState {
  // ⛔ **A REFETCH IN FLIGHT IS `loading`** — the single most important line in this file.
  // React Query keeps the last successful `data` on the query object across a refetch AND across a
  // refetch that FAILS. Without this, a reader whose entitlement was revoked kept seeing the
  // previous open's FILENAMES, with a live Download button, for as long as the component stayed
  // mounted — the 403 never reached the screen because `data` was still there (review BLOCKER 1).
  // A filename is content; "Project Falcon CIM.pdf" is itself the disclosure.
  if (q.isLoading || q.isFetching === true) return 'loading';
  const status = q.error instanceof ApiError ? q.error.status : null;
  // 403 and 404 are their own branches rather than a shared "no content": this surface renders
  // nothing for either (the body card above it already tells the entitlement story, and a 404 is
  // the feature flag being off), but an explicit branch is what stops a future edit from turning
  // one of them into "Failed to load attachments."
  if (status === 403) return 'forbidden';
  if (status === 404) return 'missing';
  if (status != null) return 'error';
  if (q.isError === true || q.error != null) return 'error';
  if (q.isSuccess === true && q.data != null) return 'ready';
  // No data, no error, not fetching — a disabled or idle query. Render nothing rather than sit on
  // "Checking permissions…", which on a governed surface reads as "still deciding about you".
  return 'missing';
}
